import { useState } from "react";
import { getSubscription, changePlan } from "../api/enterprise.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

type UsageReport = {
  subscription: {
    id: string;
    orgId: string;
    planTier: string;
    status: string;
    aiTokensUsed: number;
    apiCallsUsed: number;
    storageBytesUsed: number;
    quotaResetsAt: string;
  };
  limits: {
    maxMembers: number;
    aiTokensPerMonth: number;
    apiCallsPerMonth: number;
    storageBytesTotal: number;
    maxApiKeys: number;
    maxWebhooks: number;
  };
  utilizationPct: { aiTokens: number; storage: number; apiCalls: number };
};

const PLANS = ["FREE", "STARTUP", "BUSINESS", "ENTERPRISE"];

function Bar({ pct }: { pct: number }) {
  return (
    <div className="h-2 w-full rounded bg-gray-800">
      <div
        className={`h-2 rounded transition-all ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-yellow-500" : "bg-green-500"}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
}

export function AdminSubscriptionPage() {
  const { token } = useAuth();
  const [orgId, setOrgId] = useState("");
  const [report, setReport] = useState<UsageReport | null>(null);

  async function load() {
    if (!orgId) return;
    const res = (await getSubscription(token, orgId)) as UsageReport;
    setReport(res);
  }

  async function handleChangePlan(plan: string) {
    if (!orgId) return;
    if (!window.confirm(`Change plan to ${plan}? This affects quota limits immediately.`)) return;
    await changePlan(token, orgId, plan);
    await load();
  }

  const fmt = (n: number) => (n === -1 ? "Unlimited" : n.toLocaleString());

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold text-white">Subscription &amp; Usage</h1>

        <div className="rounded border border-yellow-700 bg-yellow-900 p-3 text-sm text-yellow-200">
          Plan changes require human confirmation. All plan upgrades/downgrades are subject to
          billing policy.
        </div>

        <div className="flex gap-3">
          <input
            className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
            placeholder="Organization ID"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          />
          <button
            onClick={load}
            className="rounded bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
          >
            Load
          </button>
        </div>

        {report && (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {[
                { label: "Plan", value: report.subscription.planTier },
                { label: "Status", value: report.subscription.status },
                {
                  label: "Quota Resets",
                  value: new Date(report.subscription.quotaResetsAt).toLocaleDateString(),
                },
                { label: "Max Members", value: fmt(report.limits.maxMembers) },
              ].map(({ label, value }) => (
                <div key={label} className="rounded border border-gray-800 bg-gray-900 p-4">
                  <div className="text-xs text-gray-500">{label}</div>
                  <div className="mt-1 font-semibold text-white">{value}</div>
                </div>
              ))}
            </div>

            <div className="space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-5">
              <h2 className="text-lg font-semibold text-white">Usage</h2>
              {[
                {
                  label: "AI Tokens",
                  used: report.subscription.aiTokensUsed,
                  limit: report.limits.aiTokensPerMonth,
                  pct: report.utilizationPct.aiTokens,
                },
                {
                  label: "API Calls",
                  used: report.subscription.apiCallsUsed,
                  limit: report.limits.apiCallsPerMonth,
                  pct: report.utilizationPct.apiCalls,
                },
                {
                  label: "Storage",
                  used: report.subscription.storageBytesUsed,
                  limit: report.limits.storageBytesTotal,
                  pct: report.utilizationPct.storage,
                },
              ].map(({ label, used, limit, pct }) => (
                <div key={label}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="text-gray-300">{label}</span>
                    <span className="text-gray-500">
                      {used.toLocaleString()} / {fmt(limit)} ({pct}%)
                    </span>
                  </div>
                  <Bar pct={pct} />
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
              <h2 className="mb-3 text-lg font-semibold text-white">Change Plan</h2>
              <div className="flex flex-wrap gap-3">
                {PLANS.map((plan) => (
                  <button
                    key={plan}
                    onClick={() => handleChangePlan(plan)}
                    className={`rounded px-4 py-2 text-sm font-medium ${report.subscription.planTier === plan ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"}`}
                  >
                    {plan}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
